import React, { useEffect, useMemo, useState } from "react";
import { StyleSheet, View, Platform } from "react-native";
import { MagnifyingGlass } from "../components/svgs/common";
import CustomText from "../components/common/general/CustomText/CustomText";
import { MotiView } from "moti";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import NoChatBarLayout from "../layouts/NoChatBarLayout.web";
import {
  GetAssessmentByFacultyDocument,
  GetGradingCenterDashboardDataDocument,
  GetUserSubmissionsByFacultyDocument,
  useGetAssessmentByFacultyQuery,
  useGetAssessmentSubmissionsDetailsQuery,
  useGetCoursesByFacultyQuery,
  useGetGradingCenterDashboardDataQuery,
  useGetUserSubmissionsByFacultyQuery,
  useMeQuery,
  useUpdateAssessmentGradeMutation,
  useUpdateStudentAssignmentGradeMutation,
} from "../graphql/generated/graphql";
import DashboardDataContainer from "../containers/customCoursework/TeacherGradingUIComponents/DashboardDataContainer";
import GradingTableContainer from "../containers/customCoursework/TeacherGradingUIComponents/GradingsTableContainer";
import CustomMultiSelect from "../components/common/general/CustomMultiSelect/CustomMultiSelect";
import CustomTextInput from "../components/common/general/CustomTextInput/CustomTextInput";
import AssessmentGradingModalContainer from "../containers/customCoursework/TeacherGradingUIComponents/AssessmentGradingModalContainer";
import AssignmentSubmissionGradingModal from "../containers/customCoursework/TeacherGradingUIComponents/AssignmentSubmissionGradingModalContainer";

enum ModalState {
  CLOSED = "CLOSED",
  VIEW_ASSESSMENT_RESPONSE = "VIEW_ASSESSMENT_RESPONSE",
  VIEW_ASSIGNMENT_RESPONSE = "VIEW_ASSIGNMENT_RESPONSE",
  EDIT_SCORE = "EDIT_SCORE",
}

export interface GradingData {
  userId?: string;
  userName?: string;
  isCompleted?: boolean;
  completedAt?: string;
  isRunning?: boolean;
  startedAt?: string;
  totalPoints?: number;
  maximumPoints?: number;
  hasTeacherGraded?: boolean;
  assessmentId?: string;
  created?: string;
  lastModified?: string;
  moduleName?: string;
  courseName?: string;
  type?: string;
  isEnabled?: boolean;
  id?: string;
  questions?: {
    id: string;
    question: string;
    totalScore: number;
    rubrics: {
      id: string;
      component: string;
      points: number;
      pointsAwarded: number;
      maxPoints: number;
      remarks: string;
    }[];
    userAnswer: {
      answer: string;
      createdAt: string;
      totalPoints: number;
      maximumPoints: number;
    }[];
  }[];
}

const GradingCenterView = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [assessmentsPage, setAssessmentsPage] = useState(1);
  const [submissionsPage, setSubmissionsPage] = useState(1);
  const [pageSize, setPageSize] = useState(1000);
  const [modalState, setModalState] = React.useState<ModalState>(ModalState.CLOSED);
  const [selectedGradingData, setSelectedGradingData] = React.useState<GradingData | null>(null);
  const [selectedCourses, setSelectedCourses] = React.useState<string[]>([]);
  const [selectedModules, setSelectedModules] = React.useState<string[]>([]);

  const { data: meData } = useMeQuery();

  const [updateAssessmentGrade] = useUpdateAssessmentGradeMutation({
    refetchQueries: [
      {
        query: GetAssessmentByFacultyDocument,
        variables: {
          input: {
            facultyId: meData?.me?.id || "",
            query: searchQuery,
            page: assessmentsPage,
            pageSize: pageSize,
            courseIds: selectedCourses,
            moduleIds: selectedModules,
          },
        },
      },
      {
        query: GetGradingCenterDashboardDataDocument,
        variables: {
          facultyId: meData?.me?.id || "",
        },
      },
    ],
  });
  const [updateStudentAssignmentGrade] = useUpdateStudentAssignmentGradeMutation({
    refetchQueries: [
      {
        query: GetUserSubmissionsByFacultyDocument,
        variables: {
          input: {
            facultyId: meData?.me?.id || "",
            query: searchQuery,
            page: submissionsPage,
            pageSize: pageSize,
            courseIds: selectedCourses,
            moduleIds: selectedModules,
          },
        },
      },
      {
        query: GetGradingCenterDashboardDataDocument,
        variables: {
          facultyId: meData?.me?.id || "",
        },
      },
    ],
  });

  const { data: courseData, loading: courseLoading } = useGetCoursesByFacultyQuery({
    variables: {
      userId: meData?.me?.id,
    },
    skip: !meData?.me?.id,
  });

  const { data: gradingCenterDashboardData, loading: gradingCenterDashboardLoading } =
    useGetGradingCenterDashboardDataQuery({
      variables: {
        facultyId: meData?.me?.id,
      },
      skip: !meData?.me?.id,
    });
  const gradingData = gradingCenterDashboardData?.getGradingCenterDashboardData?.data;

  const { data: assessmentSubmissionDetails, loading: assessmentSubmissionDetailsLoading } =
    useGetAssessmentSubmissionsDetailsQuery({
      variables: {
        assessmentId: selectedGradingData?.assessmentId,
        userId: selectedGradingData?.userId,
      },
      skip: !selectedGradingData?.assessmentId || !selectedGradingData?.userId,
    });

  const { data: assessmentData, refetch: refetchAssessmentData } = useGetAssessmentByFacultyQuery({
    variables: {
      input: {
        facultyId: meData?.me?.id || "",
        query: searchQuery,
        page: assessmentsPage,
        pageSize: pageSize,
        courseIds: selectedCourses,
        moduleIds: selectedModules,
      },
    },
    skip: !meData?.me?.id,
  });

  const { data: userSubmissionsData, refetch: refetchUserSubmissionsData } = useGetUserSubmissionsByFacultyQuery({
    variables: {
      input: {
        facultyId: meData?.me?.id || "",
        query: searchQuery,
        page: submissionsPage,
        pageSize: pageSize,
        courseIds: selectedCourses,
        moduleIds: selectedModules,
      },
    },
    skip: !meData?.me?.id,
  });

  const filters = useMemo(() => {
    return {
      courses: courseData?.getCoursesByFaculty?.data?.map((course) => ({
        key: course.id,
        value: course.name,
      })),
      modules: courseData?.getCoursesByFaculty?.data
        ?.map((course) =>
          course.modules.map((module) => ({
            key: module.id,
            value: module.title,
          }))
        )
        .flat(),
    };
  }, [courseData]);

  useEffect(() => {
    setSelectedCourses(filters.courses?.map((course) => course.key));
    setSelectedModules(filters.modules?.map((module) => module.key));
  }, [courseData]);

  useEffect(() => {
    refetchAssessmentData();
    refetchUserSubmissionsData();
  }, [refetchAssessmentData, refetchUserSubmissionsData, meData]);

  const assessments = useMemo(
    () =>
      assessmentData?.getAssessmentByFaculty?.data?.assessments.map((assessment) => ({
        ...assessment,
        type: "Assessment",
      })),
    [assessmentData]
  );

  const submissions = useMemo(
    () =>
      userSubmissionsData?.getUserSubmissionsByFaculty?.data?.submissions.map((submission) => ({
        ...submission,
        type: "Assignment",
      })),
    [userSubmissionsData]
  );

  const handleViewResponse = (item: GradingData) => {
    setSelectedGradingData(item);
    // Make sure we're properly checking the type
    if (item.type === "Assessment") {
      setModalState(ModalState.VIEW_ASSESSMENT_RESPONSE);
    } else {
      setModalState(ModalState.VIEW_ASSIGNMENT_RESPONSE);
    }
  };

  const handleSaveGrade = async (gradingData: GradingData, newScore: number, feedback?: string) => {
    setIsSubmitting(true);

    if (gradingData.type === "Assessment") {
      await updateAssessmentGrade({
        variables: {
          assessmentId: gradingData.assessmentId,
          grade: newScore,
          userId: gradingData.userId,
        },
      });
    } else {
      await updateStudentAssignmentGrade({
        variables: {
          submissionId: gradingData.id,
          grade: newScore,
          feedback,
        },
      });
    }
    setModalState(ModalState.CLOSED);
    setIsSubmitting(false);
  };

  const handleCloseModal = () => {
    setModalState(ModalState.CLOSED);
    setSelectedGradingData(null);
  };

  const handleAssessmentsPageChange = (newPage: number) => {
    const totalPageCount = Math.ceil(assessmentData?.getAssessmentByFaculty?.data?.totalCount / pageSize);
    if (newPage >= 1 && newPage <= totalPageCount) {
      setAssessmentsPage(newPage);
    }
  };

  const handleSubmissionsPageChange = (newPage: number) => {
    const totalPageCount = Math.ceil(userSubmissionsData?.getUserSubmissionsByFaculty?.data?.totalCount / pageSize);
    if (newPage >= 1 && newPage <= totalPageCount) {
      setSubmissionsPage(newPage);
    }
  };

  useEffect(() => {
    setAssessmentsPage(1);
    setSubmissionsPage(1);
  }, [searchQuery]);

  useEffect(() => {
    refetchAssessmentData();
    refetchUserSubmissionsData();
  }, [searchQuery, assessmentsPage, submissionsPage, pageSize]);

  return (
    <NoChatBarLayout>
      <MotiView
        from={{ opacity: 0, translateY: 20 }}
        animate={{ opacity: 1, translateY: 0 }}
        delay={10}
        // @ts-expect-error - MotiView transition props have complex typing that's incompatible with simple objects
        transition={{ type: "timing", duration: 500 }}>
        <CustomText size="m" textType="display" text="Grading Center" weight="ultraBold" />
      </MotiView>
      <CustomSpacing type="vertical" size="xl" />
      <View style={styles.contentContainer}>
        <DashboardDataContainer
          newSubmissionsToday={gradingData?.newSubmissionsToday}
          assignmentsAndAssessmentsToGrade={gradingData?.assignmentsAndAssessmentsToGrade}
          isLoading={gradingCenterDashboardLoading || gradingData === undefined}
        />
        <View style={styles.filterContainer}>
          <View style={styles.searchContainer}>
            <MagnifyingGlass style={styles.searchIcon} />
            <CustomTextInput
              placeholder="Search by any value"
              value={searchQuery}
              onChangeText={setSearchQuery}
              style={[
                styles.searchInput,
                Platform.select({
                  web: {
                    outlineStyle: "none",
                    outlineWidth: 0,
                    WebkitAppearance: "none",
                  } as unknown,
                }),
              ]}
              placeholderTextColor="#9CA3AF"
              selectionColor="#6B7280"
              cursorColor="#6B7280"
            />
          </View>
          <View style={styles.dropdownFilterContainer}>
            <CustomMultiSelect
              data={filters.courses}
              selectedItems={selectedCourses}
              setSelectedItems={setSelectedCourses}
              label="Course"
            />
            <CustomMultiSelect
              data={filters.modules}
              selectedItems={selectedModules}
              setSelectedItems={setSelectedModules}
              label="Module"
            />
          </View>
        </View>
        <View style={styles.tableContainer}>
          <CustomText text="Assessments" style={styles.tableTitle} weight="bold" />
          <GradingTableContainer
            gradingData={assessments ?? []}
            page={assessmentsPage}
            pageSize={pageSize}
            handlePageChange={handleAssessmentsPageChange}
            totalCount={assessmentData?.getAssessmentByFaculty?.data?.totalCount}
            handleRowPress={handleViewResponse}
          />
        </View>
        <CustomSpacing type="vertical" size="xl" />
        <View style={styles.tableContainer}>
          <CustomText text="Assignment Submissions" style={styles.tableTitle} weight="bold" />
          <GradingTableContainer
            gradingData={submissions ?? []}
            page={submissionsPage}
            pageSize={pageSize}
            handlePageChange={handleSubmissionsPageChange}
            totalCount={userSubmissionsData?.getUserSubmissionsByFaculty?.data?.totalCount}
            handleRowPress={handleViewResponse}
          />
        </View>
      </View>
      {modalState === ModalState.VIEW_ASSESSMENT_RESPONSE && (
        <AssessmentGradingModalContainer
          isVisible={true}
          onClose={handleCloseModal}
          isLoading={assessmentSubmissionDetailsLoading}
          gradingData={{
            ...(assessmentSubmissionDetails?.getAssessmentSubmissionDetails?.data as GradingData),
            type: "Assessment",
          }}
          onSave={handleSaveGrade}
          isSubmitting={isSubmitting}
        />
      )}

      {modalState === ModalState.VIEW_ASSIGNMENT_RESPONSE && (
        <AssignmentSubmissionGradingModal
          isVisible={true}
          onClose={handleCloseModal}
          gradingData={selectedGradingData}
          onSave={handleSaveGrade}
          isSubmitting={isSubmitting}
        />
      )}
    </NoChatBarLayout>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    position: "relative",
    zIndex: 1,
  },
  filterContainer: {
    backgroundColor: "#f9fafb",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    zIndex: 50,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 6,
    padding: 4,
    backgroundColor: "#FFF",
  },
  searchIcon: {
    marginRight: 8,
    color: "#3e68fe",
  },
  searchInput: {
    borderWidth: 0,
    backgroundColor: "transparent",
    width: 200,
    padding: 8,
    fontSize: 14,
    color: "#374151",
  },
  dropdownFilterContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    position: "relative",
    zIndex: 50,
  },
  tableContainer: {
    marginTop: 12,
    paddingHorizontal: 12,
  },
  tableTitle: {
    fontSize: 16,
    color: "#344054",
  },
});

export default GradingCenterView;
